class VideoModal {
  constructor(options = {}) {
    this.modal = options.modal || document.querySelector('.video-slider-modal');
    if (!this.modal) return;

    this.player = this.modal.querySelector('.video-slider-modal-player');
    this.videos = this.player.querySelectorAll('.video-slider-video');
    this.playButton = this.player.querySelector('.video-slider-slide-play-button');
    this.currentIndex = 0;
    this.currentVideo = null;

    this.isVariantMode = options.isVariantMode;
    this.thumbnailSelector = options.thumbnailSelector || '.video-thumbnail';
    this.videoSelector = options.videoSelector;

    this.initVideos();
    this.bindEvents();
  }

  initVideos() {
    this.videos.forEach(video => {
      video.style.display = 'none';
    });

    if (this.playButton) {
      this.playButton.style.display = 'none';
    }
  }

  bindEvents() {
    const thumbnails = document.querySelectorAll(this.thumbnailSelector);

    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        if (this.isVariantMode) {
          const variantId = e.currentTarget.dataset.variantId;
          const videoIndex = Array.from(this.videos).findIndex(v => v.dataset.variantId === variantId);
          if (videoIndex !== -1) {
            this.open(videoIndex);
          }
        } else {
          const index = parseInt(e.currentTarget.dataset.videoIndex);
          this.open(index);
        }
      });
    });

    const backdrop = this.modal.querySelector('.video-slider-modal-backdrop');
    const container = this.modal.querySelector('.video-slider-modal-container');
    const closeBtn = this.modal.querySelector('.video-slider-modal-close');

    if (backdrop) backdrop.addEventListener('click', () => this.close());
    if (container) {
      container.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) {
          this.close();
        }
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    if (!this.isVariantMode) {
      const prevBtn = this.modal.querySelector('.video-slider-modal-prev');
      const nextBtn = this.modal.querySelector('.video-slider-modal-next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
      if (nextBtn) nextBtn.addEventListener('click', () => this.next());
    }

    if (this.playButton) {
      this.playButton.addEventListener('click', () => {
        if (this.currentVideo) {
          this.currentVideo.play();
          this.playButton.style.display = 'none';
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;
      if (this.isTypingTarget(e)) return;

      const keyActions = {
        'Escape': () => this.close(),
        ' ': () => {
          e.preventDefault();
          this.togglePlayPause();
        }
      };

      if (!this.isVariantMode) {
        keyActions['ArrowLeft'] = () => this.prev();
        keyActions['ArrowRight'] = () => this.next();
      }

      const action = keyActions[e.key];
      if (action) action();
    });
  }

  bindVideoEvents(video) {
    video.onclick = () => this.togglePlayPause();
    video.onpause = () => {
      if (this.playButton) this.playButton.style.display = 'block';
    };
    video.onplay = () => {
      if (this.playButton) this.playButton.style.display = 'none';
    };

    if (!this.isVariantMode) {
      video.onended = () => this.autoAdvance();
    }
  }

  togglePlayPause() {
    if (!this.currentVideo) return;

    if (this.currentVideo.paused) {
      this.currentVideo.play();
    } else {
      this.currentVideo.pause();
    }
  }

  autoAdvance() {
    const nextIndex = this.currentIndex < this.videos.length - 1 ? this.currentIndex + 1 : 0;
    this.showVideo(nextIndex);
    this.currentIndex = nextIndex;
  }

  resetVideo(video) {
    video.pause();
    video.currentTime = 0;
    video.style.display = 'none';
  }

  open(index) {
    this.currentIndex = index;
    this.showVideo(index);
    this.modal.style.display = 'block';

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
  }

  close() {
    this.modal.style.display = 'none';

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

    this.videos.forEach(video => this.resetVideo(video));
    if (this.playButton) this.playButton.style.display = 'none';
    this.currentVideo = null;
  }

  prev() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.videos.length - 1;
    this.showVideo(newIndex);
    this.currentIndex = newIndex;
  }

  next() {
    const newIndex = this.currentIndex < this.videos.length - 1 ? this.currentIndex + 1 : 0;
    this.showVideo(newIndex);
    this.currentIndex = newIndex;
  }

  showVideo(index) {
    if (this.currentVideo) {
      this.resetVideo(this.currentVideo);
    }

    this.currentVideo = this.videos[index];
    this.currentVideo.style.display = 'block';

    this.bindVideoEvents(this.currentVideo);

    this.currentVideo.play().catch(e => {
      if (this.playButton) this.playButton.style.display = 'block';
    });
  }

  isOpen() {
    // Read the resolved style, not the inline one: the modal starts hidden from
    // the stylesheet, so this.modal.style.display is "" until close() first runs.
    return getComputedStyle(this.modal).display !== 'none';
  }

  isTypingTarget(e) {
    // composedPath()[0] rather than e.target: an event raised inside a shadow root
    // retargets to its host by the time it reaches document, so e.target reads as
    // a plain DIV and the usual "is the visitor typing?" check misses it.
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
    const target = path[0] || e.target;
    if (!target) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const videoSliderModal = document.querySelector('.video-slider-modal:not([data-variant-video-modal])');
  if (videoSliderModal) {
    new VideoModal({
      modal: videoSliderModal,
      thumbnailSelector: '.video-thumbnail[data-video-index]'
    });
  }

  const videoSlider = document.querySelector('.video-slider-swiper');
  if (videoSlider) {
    const slidesMobile = parseFloat(videoSlider.dataset.slidesMobile) || 3;
    const slidesDesktop = parseFloat(videoSlider.dataset.slidesDesktop) || 4;

    new Swiper(videoSlider, {
      slidesPerView: slidesMobile,
      spaceBetween: 5,
      allowTouchMove: true,
      navigation: {
        nextEl: '.video-slider-modal-button-next, .video-slider-side-next',
        prevEl: '.video-slider-modal-button-prev, .video-slider-side-prev',
      },
      breakpoints: {
        989: {
          slidesPerView: slidesDesktop,
          spaceBetween: 5
        }
      }
    });
  }
});